import { useState } from 'react';
import { motion } from 'framer-motion';
import { Edit2, LogOut, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const ProfilePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isEditingName, setIsEditingName] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [googleConnected, setGoogleConnected] = useState(true);
  const [githubConnected, setGithubConnected] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleLogout = async () => {
    navigate('/');
    await logout();
  };

  const handleSaveName = () => {
    console.log('Saving name:', name);
    setIsEditingName(false);
  };

  const handleGoogleToggle = () => {
    if (googleConnected) {
      if (window.confirm('Are you sure you want to disconnect Google?')) {
        setGoogleConnected(false);
      }
    } else {
      setGoogleConnected(true);
    }
  };

  const handleGithubConnect = () => {
    setGithubConnected(!githubConnected);
  };

  const handleDeleteAccount = () => {
    if (showDeleteConfirm) {
      alert('Account deletion initiated. This is a mock action.');
      handleLogout();
    } else {
      setShowDeleteConfirm(true);
      setTimeout(() => setShowDeleteConfirm(false), 5000);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-4xl font-extrabold text-[#0F172A] mb-2 text-center tracking-[-0.02em]">Profile</h1>
        <p className="text-[#475569] mb-12 text-center">
          Manage your account information
        </p>

        {/* User Info Card */}
        <div
          className="rounded-2xl bg-white border border-[#E2E8F0] p-8 mb-6"
          style={{ boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -2px rgba(0,0,0,0.05)' }}
        >
          <div className="flex flex-col items-center gap-6">
            <div className="w-20 h-20 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-3xl font-extrabold"
              style={{ boxShadow: '0 4px 14px 0 rgba(37, 99, 235, 0.39)' }}
            >
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="text-center w-full">
              {isEditingName ? (
                <div className="flex items-center justify-center gap-2">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="px-4 py-2 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] text-[#0F172A] text-xl font-semibold outline-none focus:border-[#2563EB] text-center transition-colors"
                  />
                  <button
                    onClick={handleSaveName}
                    className="px-4 py-2 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-semibold transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingName(false);
                      setName(user?.name || '');
                    }}
                    className="px-4 py-2 rounded-lg border border-[#E2E8F0] text-[#475569] hover:text-[#0F172A] text-sm font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <h2 className="text-2xl font-bold text-[#0F172A]">
                    {name || 'User'}
                  </h2>
                  <button
                    onClick={() => setIsEditingName(true)}
                    className="p-1.5 rounded hover:bg-[#F8FAFC] transition-colors"
                  >
                    <Edit2 className="w-5 h-5 text-[#475569]" />
                  </button>
                </div>
              )}
              <p className="text-[#475569] mt-2">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-2.5 rounded-lg border-2 border-[#E2E8F0] text-[#475569] hover:text-[#2563EB] hover:border-[#2563EB] transition-all font-semibold"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>

        {/* Connected Accounts */}
        <div
          className="rounded-2xl bg-white border border-[#E2E8F0] p-8 mb-6"
          style={{ boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -2px rgba(0,0,0,0.05)' }}
        >
          <h3 className="text-xl font-bold text-[#0F172A] mb-2 flex items-center justify-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#10B981]" />
            Connected Accounts
          </h3>
          <p className="text-[#475569] text-sm mb-8 text-center">
            Connect multiple sign-in methods to your account
          </p>

          <div className="space-y-4 max-w-xl mx-auto">
            {/* Google */}
            <div className="flex items-center justify-between p-5 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white border border-[#E2E8F0] flex items-center justify-center">
                  <span className="text-xl font-bold text-[#0F172A]">G</span>
                </div>
                <span className="text-[#0F172A] font-semibold text-lg">Google</span>
              </div>
              <div className="flex items-center gap-3">
                {googleConnected ? (
                  <>
                    <span className="text-[#10B981] text-sm font-semibold">Connected</span>
                    <button
                      onClick={handleGoogleToggle}
                      className="text-[#475569] hover:text-[#EF4444] text-sm transition-colors font-medium"
                    >
                      Disconnect
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleGoogleToggle}
                    className="px-4 py-2 rounded-lg text-[#2563EB] hover:bg-[#EFF6FF] text-sm font-semibold transition-colors"
                  >
                    Connect
                  </button>
                )}
              </div>
            </div>

            {/* GitHub */}
            <div className="flex items-center justify-between p-5 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#0F172A] flex items-center justify-center">
                  <span className="text-xl font-bold text-white">G</span>
                </div>
                <span className="text-[#0F172A] font-semibold text-lg">GitHub</span>
              </div>
              <div className="flex items-center gap-3">
                {githubConnected ? (
                  <>
                    <span className="text-[#10B981] text-sm font-semibold">Connected</span>
                    <button
                      onClick={handleGithubConnect}
                      className="text-[#475569] hover:text-[#EF4444] text-sm transition-colors font-medium"
                    >
                      Disconnect
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleGithubConnect}
                    className="px-4 py-2 rounded-lg text-[#2563EB] hover:bg-[#EFF6FF] text-sm font-semibold transition-colors"
                  >
                    Connect
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="rounded-2xl bg-red-50 border border-red-200 p-8">
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-2 text-[#EF4444]">
              <span className="text-2xl">⚠️</span>
              <h3 className="text-xl font-bold">Danger Zone</h3>
            </div>
            <div className="text-center">
              <h4 className="text-[#0F172A] font-semibold mb-2">Delete account</h4>
              <p className="text-[#475569] text-sm mb-4">
                Permanently delete your account and all data
              </p>
            </div>
            <button
              onClick={handleDeleteAccount}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg border-2 font-semibold transition-all ${
                showDeleteConfirm
                  ? 'bg-[#EF4444] border-[#EF4444] text-white hover:bg-red-600'
                  : 'bg-transparent border-red-300 text-[#EF4444] hover:bg-red-100'
              }`}
            >
              <Trash2 className="w-5 h-5" />
              {showDeleteConfirm ? 'Click again to confirm' : 'Delete account'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ProfilePage;
